import { AppRoutes } from '@core/AppRoutes'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { queryClient } from '@core/Data/ReactQuery'
import { appHistory } from '@core/Utils/routing/history'
import { addToast, removeToast } from '@mobi/component-library/Feedback/Toast'
import type { ToastItem } from '@mobi/component-library/Feedback/Toast/types'
import { trackShareBetError } from '@core/Areas/ShareBet/helpers/analytics'
import { trackShareBetClick } from '@core/Areas/ShareBet/ShareBetOutgoing/Components/ShareWithExternal/analytics'
import type {
  BetLoopPostMultipleKambiSharesResponse,
  BetLoopPostMultipleSharesResponse,
} from '@core/Areas/BetLoop/helpers/api/types'
import type { SelectedKambiTickets, SelectedTickets } from '@core/Areas/BetLoop/Components/QuickShare/types'
import type { KambiSportsShare, TicketShare } from '@core/Areas/ShareBet/types'

const enum LocalConstants {
  QuickShareToastId = 'bet-loop-quick-share',
}

export function handleShareToBetLoopMutateResponse({
  racingResponse,
  kambiResponse,
  selectedTickets,
  selectedKambiTickets,
}: {
  racingResponse?: BetLoopPostMultipleSharesResponse
  kambiResponse?: BetLoopPostMultipleKambiSharesResponse
  selectedTickets: SelectedTickets
  selectedKambiTickets: SelectedKambiTickets
}) {
  const hasSuccessfulPosts =
    (racingResponse && racingResponse?.successfulShares > 0) ||
    (kambiResponse && kambiResponse?.successfulShares > 0)

  const hasFailedPosts =
    (racingResponse && racingResponse?.failedShares > 0) ||
    (kambiResponse && kambiResponse?.failedShares > 0)

  const wereAllPostsSuccessful = hasSuccessfulPosts && !hasFailedPosts

  if (racingResponse) {
    const { results } = racingResponse

    // Track failed shares
    if (hasFailedPosts) {
      selectedTickets.forEach(ticket => {
        const matchedResult = results?.find(
          result => result.ticketSequence === ticket.ticketSequence
        )
        if (matchedResult?.success) return // Skip successful shares

        const errorMessage = matchedResult?.errorMessage || 'Oops, unable to share to Bet Loop'
        trackShareBetError({
          message: errorMessage,
          destination: 'Bet Loop',
          location: 'bet-loop',
          betType: ticket.productName,
        })
      })
    }

    if (hasSuccessfulPosts) {
      selectedTickets.forEach(ticket => {
        const matchedResult = results?.find(
          result => result.ticketSequence === ticket.ticketSequence
        )
        if (!matchedResult?.success) return
        const ticketShare: TicketShare = {
          contextType: 'ticket',
          share: { ticket, activeTab: 'pending', ticketSequence: ticket.ticketSequence },
        }
        trackShareBetClick({
          data: ticketShare,
          isCostShared: false,
          destination: 'Bet Loop',
          sourceLocation: 'bet-loop',
        })
      })
    }
  }

  if (kambiResponse) {
    const { results } = kambiResponse

    // Track failed shares
    if (hasFailedPosts) {
      selectedKambiTickets.forEach(ticket => {
        const matchedResult = results?.find(
          result => result.betRef === ticket.coupon.bets[0].betRef
        )
        if (matchedResult?.success) return // Skip successful shares

        const errorMessage = matchedResult?.errorMessage || 'Oops, unable to share to Bet Loop'
        trackShareBetError({
          message: errorMessage,
          destination: 'Bet Loop',
          location: 'bet-loop',
          betType: 'sport',
        })
      })
    }

    if (hasSuccessfulPosts) {
      selectedKambiTickets.forEach(ticket => {
        const matchedResult = results?.find(
          result => result.betRef === ticket.coupon.bets[0].betRef
        )
        if (!matchedResult?.success) return
        const ticketShare: KambiSportsShare = {
          contextType: 'kambi',
          share: { coupon: ticket.coupon },
        }
        trackShareBetClick({
          data: ticketShare,
          isCostShared: false,
          destination: 'Bet Loop',
          sourceLocation: 'bet-loop',
        })
      })
    }
  }

  let button: ToastItem['button']
  const icon = wereAllPostsSuccessful ? 'success' : hasSuccessfulPosts ? 'warning' : 'error'
  const successfulShares =
    (racingResponse ? racingResponse.successfulShares : 0) +
    (kambiResponse ? kambiResponse.successfulShares : 0)
  const title = hasSuccessfulPosts
    ? `${successfulShares} Bet${successfulShares > 1 ? 's' : ''} shared to Bet Loop`
    : 'Oops, unable to share to Bet Loop'

  if (hasSuccessfulPosts) {
    queryClient.invalidateQueries({
      queryKey: queryKeys.betLoopSharedBetsFeedBase(),
      refetchActive: true,
    })
    button = {
      text: 'View',
      onClick() {
        appHistory.push(AppRoutes.BetLoopMyProfile)
        removeToast({ id: LocalConstants.QuickShareToastId })
      },
    }
  }

  addToast({
    id: LocalConstants.QuickShareToastId,
    timeoutMs: 8_000,
    title,
    icon,
    button,
    isDismissable: !button,
    theme: 'dark',
  })
}
